import { type Access } from '@atlaskit/linking-types';
import { type SerializedError } from '@databases/types';
import {
  type CloudId,
  type JiraIssueRef,
  type JiraServer,
  type JiraServerId,
  type ResolvedJiraIssueRef,
} from '@databases/types/confluence';
import { uniq } from 'lodash-es';

import { type PayloadAction, createSlice } from '../../shims/redux-toolkit';
import { type AsyncOperation, AsyncOperationStatus } from '../redux';

export interface JiraState {
  /** Loaded issues by issue key by server ID */
  issues: Record<string, Record<string, ResolvedJiraIssueRef>>;
  /** Issue status and access control by issue key organized by serverId or cloudId */
  issuesMetadata: Record<string, Record<string, AsyncOperation<Access>>>;

  /** Columns to load by server ID */
  columns: Record<string, string[]>;

  jiraServers?: JiraServer[] | undefined;
  jiraServersStatus?: AsyncOperation<void>;
}

const initialState: JiraState = {
  issues: {},
  issuesMetadata: {},

  columns: {},
};

const jiraSlice = createSlice({
  name: 'jira',
  initialState,
  reducers: {
    loadJiraIssuePending: (state, action: PayloadAction<JiraIssueRef>) => {
      const ref = action.payload;
      const key = getKey(ref);

      if (!state.issuesMetadata[key]) {
        state.issuesMetadata[key] = {};
      }
      state.issuesMetadata[key][ref.key] = {
        status: AsyncOperationStatus.PENDING,
      };
    },

    loadJiraIssueFinished: (
      state,
      action: PayloadAction<{ ref: JiraIssueRef; access: Access }>
    ) => {
      const { ref, access } = action.payload;
      const key = getKey(ref);

      if (!state.issuesMetadata[key]) {
        state.issuesMetadata[key] = {};
      }
      state.issuesMetadata[key][ref.key] = {
        status: AsyncOperationStatus.SUCCEEDED,
        data: access,
      };
    },

    loadJiraIssueError: (
      state,
      action: PayloadAction<JiraIssueRef & { error: SerializedError }>
    ) => {
      const { error, ...ref } = action.payload;
      const key = getKey(ref);

      if (!state.issuesMetadata[key]) {
        state.issuesMetadata[key] = {};
      }
      state.issuesMetadata[key][ref.key] = {
        status: AsyncOperationStatus.ERROR,
        error,
      };
    },

    addJiraIssuesToList: (
      state,
      action: PayloadAction<{ issues: ResolvedJiraIssueRef[] }>
    ) => {
      const { issues } = action.payload;
      for (const issue of issues) {
        const key = getKey(issue);
        if (!state.issues[key]) {
          state.issues[key] = {};
        }
        state.issues[key][issue.key] = issue;
      }
    },

    deleteJiraIssuesFromList: (
      state,
      action: PayloadAction<{ issues: JiraIssueRef[] }>
    ) => {
      const { issues } = action.payload;

      for (const issue of issues) {
        const key = getKey(issue);
        if (state.issues[key]) {
          delete state.issues[key][issue.key];
        }
      }
    },

    resetJiraServerIssues: (
      state,
      action: PayloadAction<{ cloudId: CloudId; serverId: JiraServerId }>
    ) => {
      const key = getKey(action.payload);

      delete state.issues[key];
      delete state.issuesMetadata[key];
    },

    addColumns: (
      state,
      action: PayloadAction<{
        cloudId: CloudId;
        serverId: JiraServerId;
        columns: string[];
      }>
    ) => {
      const { columns } = action.payload;
      const key = getKey(action.payload);

      state.columns[key] = uniq([...(state.columns[key] ?? []), ...columns]);
    },

    loadJiraServersPending: (state) => {
      state.jiraServersStatus = { status: AsyncOperationStatus.PENDING };
    },

    loadJiraServersFinished: (state, action: PayloadAction<JiraServer[] | undefined>) => {
      state.jiraServersStatus = { status: AsyncOperationStatus.SUCCEEDED };
      state.jiraServers = action.payload;
    },

    loadJiraServersError: (state, action: PayloadAction<{ error: SerializedError }>) => {
      const { error } = action.payload;

      state.jiraServersStatus = { status: AsyncOperationStatus.ERROR, error };
    },
  },
});

export function getKey({
  cloudId,
  serverId,
}: {
  cloudId?: CloudId;
  serverId?: JiraServerId;
}) {
  return cloudId ? cloudId : serverId ?? '';
}

export default jiraSlice;
